import React, { useMemo, useRef, useState } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import { Form, FormSection, FormActions } from 'modules/Common/form/v2';
import { useBranding } from 'modules/App/hooks/useBranding';
import { usePageAccess } from 'hooks/usePermissions';
import { useFetchDeclarant } from 'modules/Declarant/hooks/useFetchDeclarant';
import { isUkRegulation } from 'modules/Common/utils/regulation';
import { useCreateDeclarant } from 'modules/Declarant/hooks/useCreateDeclarant';
import { useUpdateDeclarant } from 'modules/Declarant/hooks/useUpdateDeclarant';
import { useUpdateRegistrationDetails } from 'modules/UkImports/hooks/useUkRegistrationSummary';
import { apiErrorMessage, issuesOf } from 'helpers/serverIssues';
import {
  DECLARANT_FIELDS,
  declarantDefaults,
  declarantPatch,
  declarantSchema,
  declarantServerField,
} from 'modules/Declarant/config/declarantInfo';
import {
  AddressFields,
  ContactFields,
  DeclarantNameField,
  EoriField,
  SicCodeField,
} from './DeclarantInfoFields';

const PROFILE_FIELDS = [
  ...DECLARANT_FIELDS.GENERAL,
  ...DECLARANT_FIELDS.CONTACT,
  ...DECLARANT_FIELDS.ADDRESS,
];
const profileSchema = declarantSchema(PROFILE_FIELDS);
// A UK declarant's business classification (backend uk-cbam-registration-summary D16).
const ukProfileSchema = declarantSchema([...PROFILE_FIELDS, 'sicCode']);

export default function DeclarantInfoForm({ onSubmit }) {
  const { t } = useTranslation('app');
  const brand = useBranding();
  const { isLoaded, organization } = useOrganization();

  const { data: declarant, isLoading: isFetching } = useFetchDeclarant();
  const { mutateAsync: createDeclarant, isPending: isCreating } = useCreateDeclarant();
  const { mutateAsync: updateDeclarant, isPending: isUpdating } = useUpdateDeclarant();
  const { mutateAsync: updateDetails, isPending: isUpdatingDetails } =
    useUpdateRegistrationDetails();
  // The SIC code is written only through the UK route, gated by imports:edit (D3).
  const { canEdit: canEditImports } = usePageAccess('imports');

  const existingDeclarant = !!declarant;
  const withSicCode = existingDeclarant && isUkRegulation(declarant);
  const isSaving = isCreating || isUpdating || isUpdatingDetails;
  const formRef = useRef(null);

  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const defaults = useMemo(
    () => declarantDefaults(declarant, organization),
    [declarant, organization],
  );

  // The <Form> below is keyed so react-hook-form seeds its defaults from the
  // fetched record + Clerk org once they resolve (RHF reads defaultValues only
  // at mount). We latch that key the first time both sources have settled:
  // without the latch, a window-focus revalidation (Clerk re-checking
  // `isLoaded`, or react-query refetching) would change the key, remount the
  // form, and discard whatever the user had typed.
  const formKeyRef = useRef('loading');
  if (formKeyRef.current === 'loading' && isLoaded && !isFetching) {
    formKeyRef.current = declarant?._id || 'new';
  }

  const handleSubmit = async (values) => {
    setSubmitError(null);
    setSubmitted(false);
    const { sicCode, ...fields } = values;
    const payload = {
      ...declarant,
      ...declarantPatch(fields, declarant),
      faradaiClient: brand.key === 'faradai',
    };
    try {
      if (existingDeclarant) {
        await updateDeclarant(payload);
      } else {
        await createDeclarant(payload);
      }
      // After the record: a refused SIC code lands on its field, the rest stays saved.
      if (withSicCode && canEditImports && sicCode !== (declarant.sicCode ?? '')) {
        try {
          await updateDetails({ sicCode: sicCode || null });
        } catch (err) {
          // A refused SIC code lands on its field; anything else is reported like the record save.
          const unmatched = formRef.current?.setServerIssues(err) ?? [];
          if (issuesOf(err).length === 0 || unmatched.length > 0) throw err;
          return;
        }
      }
      setSubmitted(true);
      if (onSubmit) onSubmit(true);
    } catch (err) {
      setSubmitError(
        apiErrorMessage(err) ||
          err?.response?.data?.error ||
          t('declarantInfo.genericError', 'Something went wrong'),
      );
    }
  };

  return (
    <div className="flex flex-col gap-2 mt-1">
      <h1>
        {t('declarantInfo.title', 'Declarant Information')}{' '}
        {isFetching ? (
          <FontAwesomeIcon
            icon={faSpinner}
            className="mr-2 fa-spin"
          />
        ) : null}
      </h1>

      {submitError ? (
        <div
          role="alert"
          className="relative mb-4 mt-2 rounded-md border px-4 py-3 bg-dangerSubtle text-dangerEmphasis border-dangerBorder"
        >
          <button
            type="button"
            onClick={() => setSubmitError(null)}
            aria-label={t('common:actions.close', 'Close')}
            className="absolute right-3 top-3 text-dangerEmphasis hover:opacity-70"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <h4 className="font-bold mb-1 pr-6">
            {t('declarantInfo.errorHeading', 'An error occurred')}
          </h4>
          <p className="m-0">{submitError}</p>
        </div>
      ) : null}

      <Form
        ref={formRef}
        key={formKeyRef.current}
        schema={withSicCode ? ukProfileSchema : profileSchema}
        defaultValues={defaults}
        onSubmit={handleSubmit}
        serverFieldMap={declarantServerField}
      >
        <FormSection
          title={t('declarantInfo.sectionGeneral', 'General')}
          spacing={2}
        >
          <DeclarantNameField />
          <EoriField />
          {withSicCode && <SicCodeField disabled={!canEditImports} />}
        </FormSection>

        <FormSection
          title={t('declarantInfo.sectionContact', 'Contact')}
          spacing={2}
        >
          <ContactFields />
        </FormSection>

        <FormSection
          title={t('common:entity.address', 'Address')}
          spacing={2}
        >
          <AddressFields declarant={declarant} />
        </FormSection>

        <FormActions
          submitLabel={
            onSubmit ? t('common:actions.next', 'Next') : t('common:actions.save', 'Save')
          }
          loading={isSaving}
          submitted={submitted}
          disabled={isFetching}
        />
      </Form>
    </div>
  );
}
